import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StatusChangeModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  appointment,
  currentStatus 
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // Reset selected status when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
    }
  }, [isOpen]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSubmitting]);

  // Map database status to display status
  const getDisplayStatus = (dbStatus) => {
    const statusMap = {
      'scheduled': 'Agendada',
      'confirmed': 'Confirmada',
      'in_progress': 'Em andamento',
      'completed': 'Concluída',
      'cancelled': 'Cancelada',
      'no_show': 'Não compareceu'
    };
    return statusMap?.[dbStatus] || dbStatus;
  };

  // Available status options with their colors
  const statusOptions = [
    { 
      value: 'scheduled', 
      label: 'Agendada',
      color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
      selectedColor: 'bg-yellow-500 text-white'
    },
    { 
      value: 'confirmed', 
      label: 'Confirmada',
      color: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
      selectedColor: 'bg-blue-500 text-white'
    },
    { 
      value: 'in_progress', 
      label: 'Em andamento',
      color: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      selectedColor: 'bg-purple-500 text-white'
    },
    { 
      value: 'completed', 
      label: 'Concluída',
      color: 'bg-green-100 text-green-800 hover:bg-green-200',
      selectedColor: 'bg-green-500 text-white'
    },
    { 
      value: 'cancelled', 
      label: 'Cancelada',
      color: 'bg-red-100 text-red-800 hover:bg-red-200',
      selectedColor: 'bg-red-500 text-white'
    },
    { 
      value: 'no_show', 
      label: 'Não compareceu',
      color: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
      selectedColor: 'bg-gray-500 text-white'
    }
  ];

  // Get patient name from appointment title
  const getPatientName = () => {
    const title = appointment?.title || '';
    return title?.replace(/^Consulta\s*-\s*/i, '');
  };

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    setIsSubmitting(true);
    try {
      await onSave(appointment?.id, { status: selectedStatus });
      handleClose();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-hidden"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg overflow-hidden"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Alterar Situação
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Appointment Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Paciente</span>
              <span className="text-sm font-semibold text-gray-900">{getPatientName()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Situação Atual</span>
              <span className="text-sm font-semibold text-gray-900">{getDisplayStatus(currentStatus)}</span>
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Nova Situação
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions?.map((option) => {
                const isSelected = selectedStatus === option?.value;
                const isCurrent = currentStatus === option?.value;
                
                return (
                  <button
                    key={option?.value}
                    type="button"
                    onClick={() => setSelectedStatus(option?.value)}
                    disabled={isCurrent}
                    className={`
                      px-4 py-3 rounded-lg text-sm font-medium transition-all
                      ${isCurrent 
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' 
                        : isSelected 
                          ? option?.selectedColor + ' shadow-md transform scale-105'
                          : option?.color + ' cursor-pointer'
                      }
                    `}
                  >
                    {option?.label}
                    {isCurrent && (
                      <span className="ml-1 text-xs">(Atual)</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              fullWidth
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleSubmit}
              fullWidth
              disabled={!selectedStatus || isSubmitting}
              iconName={isSubmitting ? "Loader2" : "Check"}
              iconPosition="right"
            >
              {isSubmitting ? 'Salvando...' : 'Confirmar Alteração'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;